import { useState } from 'react';
import { sms as Api, type SmsConfig } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonPage } from '../components/Shimmer';
import { Field, Row, SecretInput } from '../components/Field';

const when = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T') + 'Z');
  return isNaN(d.valueOf()) ? v
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export function MobileOtp() {
  const { data, error, loading, reload } = useAsync((signal) => Api.load(signal));
  const [f, setF] = useState<SmsConfig | null>(null);
  const [authkey, setAuthkey] = useState('');
  const [testMobile, setTestMobile] = useState('');
  const [status, setStatus] = useState('');
  const [formError, setFormError] = useState('');
  const review = useAsync((signal) => Api.reviewLogin(signal));
  const [reviewBusy, setReviewBusy] = useState(false);
  async function flipReview(on: boolean) {
    setReviewBusy(true);
    setFormError('');
    try { await Api.setReviewLogin(on); await review.reload(); }
    catch (e) { setFormError((e as Error).message); }
    setReviewBusy(false);
  }

  const cfg = f ?? data?.config ?? null;
  const set = (k: keyof SmsConfig, v: unknown) =>
    setF((p) => ({ ...(p ?? data!.config), [k]: v }) as SmsConfig);

  async function save() {
    if (!cfg) return;
    setFormError('');
    setStatus('Saving…');
    try {
      await Api.save({
        provider: cfg.provider, otp_template: cfg.otp_template,
        alert_template: cfg.alert_template, otp_var: cfg.otp_var, state: cfg.state,
        ...(authkey ? { authkey } : {}),
      });
      setAuthkey('');
      setStatus('Saved');
      setF(null);
      await reload();
    } catch (e) { setStatus(''); setFormError((e as Error).message); }
  }

  async function test() {
    if (!testMobile.trim()) { setFormError('Enter a mobile number to send the test to.'); return; }
    setFormError('');
    setStatus('Sending…');
    try {
      await Api.test(testMobile.trim());
      setStatus('Sent — check that handset.');
      await reload();
    } catch (e) { setStatus(''); setFormError((e as Error).message); }
  }

  if (loading && !data) return <SkeletonPage />;
  if (error) return <div className="pg"><div className="err">Could not load the SMS settings — {error.message}</div></div>;
  if (!data || !cfg) return null;

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Mobile &amp; OTP</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            Until this is configured, <strong>password reset does not work</strong> — a member who forgets
            theirs has to be handled by hand.
          </p>
        </div>
        <span className="tg" style={{
          background: data.configured.otp ? 'rgba(62,207,142,.14)' : 'rgba(240,100,91,.13)',
        }}>{data.configured.otp ? 'OTP ready' : 'OTP not configured'}</span>
      </div>

      {!data.can_store_secrets ? (
        <div className="err">TVX_SECRET is not set on the server, so the auth key cannot be stored safely.</div>
      ) : null}
      {formError ? <div className="err">{formError}</div> : null}

      <div className="crd" style={{ padding: '20px 22px', marginBottom: 16 }}>
        <div className="kh" style={{ marginBottom: 10 }}>Store review login</div>
        <p style={{ fontSize: 13, color: 'var(--tx2)', margin: '0 0 12px', lineHeight: 1.6 }}>
          For Google Play and App Store reviewers, who have no phone of ours to receive a code. While this is on,
          the number <strong>{review.data?.mobile ?? '1111111111'}</strong> signs in with the code{' '}
          <strong>{review.data?.code ?? '111111'}</strong>, and no SMS is sent. It opens one demo account with no plans.
          Switch it off once the review is done.
        </p>
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!review.data?.enabled} disabled={reviewBusy || !review.data}
            onChange={(e) => void flipReview(e.target.checked)} />
          <span style={{ fontSize: 13.5 }}>{review.data?.enabled ? 'On — reviewers can sign in' : 'Off'}</span>
        </label>
      </div>

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="kh" style={{ marginBottom: 14 }}>MSG91</div>
        {/* The three that decide whether a code arrives, on one line. The
            sender ID is not among them: it belongs to the approved flow. */}
        <Row>
          <Field
            label="Auth key"
            hint={cfg.has_authkey
              ? (cfg.authkey_readable ? 'Stored. Leave blank to keep it.' : 'Stored but unreadable — the key changed. Enter it again.')
              : 'Not set.'}
          >
            <SecretInput disabled={!data.can_store_secrets} value={authkey}
              placeholder={cfg.has_authkey ? 'Unchanged' : ''}
              onChange={setAuthkey} /></Field>
          <Field label="OTP template id" hint="DLT-approved, or MSG91 refuses the send.">
            <input className="inp" value={cfg.otp_template} onChange={(e) => set('otp_template', e.target.value)} /></Field>
          <Field label="OTP variable name" hint="The ##VAR## name in the template, spelled exactly as MSG91 has it.">
            <input className="inp" value={cfg.otp_var} onChange={(e) => set('otp_var', e.target.value)} /></Field>
        </Row>

        <Row cols={3}>
          <Field label="Alert template id" hint="For notification texts. Nothing sends these yet.">
            <input className="inp" value={cfg.alert_template} onChange={(e) => set('alert_template', e.target.value)} /></Field>
          <Field label="State">
            <select className="inp" value={cfg.state} onChange={(e) => set('state', e.target.value)}>
              <option value="active">active</option>
              <option value="off">off</option>
            </select></Field>
        </Row>

        <p className="hint" style={{ marginBottom: 14 }}>
          {cfg.tested_ok
            ? `Tested ${when(cfg.tested_at)} — it worked.`
            : cfg.last_error
              ? <span style={{ color: 'var(--down)' }}>Last attempt failed: {cfg.last_error}</span>
              : 'Never tested.'}
        </p>

        <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="inp" style={{ maxWidth: 220 }} placeholder="Send a test to 98…"
            value={testMobile} onChange={(e) => setTestMobile(e.target.value)} />
          <button className="b bo" type="button" onClick={() => void test()}>Send test</button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <button className="b bg" type="button" onClick={() => void save()}>Save</button>
        </div>
      </div>

      <div className="crd" style={{ padding: '20px 22px', marginTop: 18 }}>
        <div className="rowbar" style={{ marginBottom: 10 }}>
          <div className="kh">Recent messages</div>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>
            {data.stats.sent ?? 0} sent · {data.stats.pending ?? 0} waiting · {data.stats.failed ?? 0} failed
          </span>
        </div>
        {data.log.length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>Nothing has been sent yet.</p>
        ) : (
          <div className="tblx">
            <table className="tbl">
              <thead><tr><th>When</th><th>To</th><th>Message</th><th>Purpose</th><th>Status</th></tr></thead>
              <tbody>
                {data.log.map((row) => (
                  <tr key={row.id}>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>{when(row.created_at)}</td>
                    <td className="muted">{row.mobile}</td>
                    <td>{row.preview}
                      {row.error ? <div style={{ fontSize: 11.5, color: 'var(--down)' }}>{row.error}</div> : null}</td>
                    <td className="muted">{row.purpose}</td>
                    <td>
                      <span className="tg" style={{
                        background: row.status === 'sent' ? 'rgba(62,207,142,.14)'
                          : row.status === 'failed' ? 'rgba(240,100,91,.13)' : 'rgba(var(--goldrgb), .14)',
                      }}>{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
